/**
 * Public entry shells.
 *
 * LandingShell (route "/"):
 *   Marketing/landing content only. Real React controls link to the
 *   dedicated Clerk sign-up and sign-in routes. The product preview is
 *   decorative/static and uses anonymous Child A / Child B demo data.
 *
 * AuthShellLayout (routes "/sign-up/*" and "/sign-in/*"):
 *   Clean, dedicated auth shell with a single Clerk-owned card.
 */
import React, { useRef, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useSignIn, useSignUp } from "@clerk/clerk-react";
import { BellRing, CalendarCheck2, ClipboardCheck, Eye, EyeOff, ShieldCheck } from "lucide-react";

const LegalFooter = () => (
  <footer data-testid="auth-shell-footer" className="mt-10 text-center">
    <nav className="flex justify-center gap-x-4 text-[12px] text-[#7A7D7B]">
      <Link to="/privacy" data-testid="auth-shell-footer-privacy" className="hover:text-[#4A5D4E]">Privacy</Link>
      <span aria-hidden="true">·</span>
      <Link to="/terms" data-testid="auth-shell-footer-terms" className="hover:text-[#4A5D4E]">Terms</Link>
    </nav>
    <p className="mt-2 text-[11px] text-[#9AA09C]">© 2026 FrontRow Parents</p>
  </footer>
);

const BrandHeader = ({ large = false }) => (
  <div className="flex items-center gap-3">
    <img
      src="/icons/icon-192-v2.png"
      alt=""
      className={large ? "h-14 w-14 rounded-[16px]" : "h-10 w-10 rounded-[10px]"}
    />
    <div className="leading-none">
      <span className={large ? "block text-[24px] sm:text-[28px] font-extrabold text-[#172033]" : "text-[15px] font-extrabold text-[#172033]"}>
        FrontRow
      </span>
      {large && (
        <span className="mt-1.5 block text-[11px] sm:text-[12px] font-semibold tracking-[0.28em] text-[#172033]">
          PARENTS
        </span>
      )}
      {!large && <span className="ml-1 text-[15px] font-extrabold text-[#172033]">Parents</span>}
    </div>
  </div>
);

const Benefit = ({ icon: Icon, title, children, tone }) => {
  const toneClass =
    tone === "coral"
      ? "bg-[#FDEDE6] text-[#D96443]"
      : tone === "blue"
        ? "bg-[#E8F0FA] text-[#234D84]"
        : "bg-[#EAF2EC] text-[#2F6B48]";

  return (
    <div className="text-center sm:text-left lg:text-center">
      <div className={`mx-auto sm:mx-0 lg:mx-auto h-14 w-14 rounded-full flex items-center justify-center ${toneClass}`}>
        <Icon className="h-7 w-7" strokeWidth={1.8} />
      </div>
      <h3 className="mt-3 text-[16px] font-extrabold leading-tight text-[#172033]">{title}</h3>
      <p className="mt-1.5 text-[14px] leading-relaxed text-[#596273]">{children}</p>
    </div>
  );
};

const DemoChild = ({ name, tone = "green" }) => (
  <span className="inline-flex items-center gap-2 text-[12px] text-[#172033]">
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${
        tone === "blue" ? "bg-[#E8F0FA] text-[#234D84]" : "bg-[#EAF2EC] text-[#2F6B48]"
      }`}
    >
      C
    </span>
    {name}
  </span>
);

const DateTile = ({ day, date, month }) => (
  <div className="w-[62px] flex-shrink-0 rounded-2xl bg-[#F5F5F2] px-2 py-2.5 text-center text-[#596273]">
    <div className="text-[10px] font-semibold uppercase tracking-[0.08em]">{day}</div>
    <div className="mt-0.5 text-[24px] font-extrabold leading-none text-[#172033]">{date}</div>
    <div className="mt-1 text-[10px] font-semibold uppercase">{month}</div>
  </div>
);

const addDemoDays = (date, days) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const landingDateParts = (date) => ({
  weekdayShort: new Intl.DateTimeFormat("en-AU", { weekday: "short" }).format(date),
  weekdayLong: new Intl.DateTimeFormat("en-AU", { weekday: "long" }).format(date),
  day: new Intl.DateTimeFormat("en-AU", { day: "numeric" }).format(date),
  monthShort: new Intl.DateTimeFormat("en-AU", { month: "short" }).format(date),
  monthLong: new Intl.DateTimeFormat("en-AU", { month: "long" }).format(date),
  year: new Intl.DateTimeFormat("en-AU", { year: "numeric" }).format(date),
});

export const getLandingDemoDates = (now = new Date()) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return {
    today: landingDateParts(today),
    action: landingDateParts(addDemoDays(today, 1)),
    upcoming: landingDateParts(addDemoDays(today, 4)),
  };
};

const ProductPreview = () => {
  const { today, action, upcoming } = getLandingDemoDates();

  return (
  <div className="relative mx-auto w-full max-w-[330px] sm:max-w-[420px]">
    <div className="rounded-[40px] border-[6px] border-[#111820] bg-[#111820] p-1.5 shadow-[0_28px_70px_rgba(23,32,51,0.18)]">
      <div className="overflow-hidden rounded-[32px] bg-[#FCFCFA]">
        <div className="px-4 pt-4 pb-5 sm:px-6 sm:pt-5">
          <div className="flex items-center justify-between text-[10px] text-[#596273]">
            <span className="font-semibold text-[#172033]">9:41</span>
            <span className="rounded-full bg-[#F5F5F2] px-3 py-1.5">Last checked 11 min ago</span>
          </div>

          <div className="mt-4 text-[10px] font-semibold tracking-[0.16em] text-[#596273]">{`${today.weekdayLong.toUpperCase()} ${today.day} ${today.monthLong.toUpperCase()}`}</div>
          <div className="mt-2 flex items-center gap-2.5">
            <img src="/icons/icon-192-v2.png" alt="" className="h-9 w-9 rounded-[10px]" />
            <div className="text-[20px] font-extrabold text-[#172033]">FrontRow Parents</div>
          </div>
          <p className="mt-1 text-[11px] text-[#667085]">Never miss another school event.</p>

          <div className="mt-5 flex items-baseline justify-between">
            <h2 className="text-[17px] font-extrabold text-[#172033]">Today · {today.weekdayLong} {today.day} {today.monthLong}</h2>
            <span className="text-[10px] text-[#596273]">1 item</span>
          </div>

          <div className="mt-4 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#2F6B48]">Happening today</div>
          <div className="mt-2 rounded-2xl border border-[#E6E8EB] bg-white p-3.5 shadow-sm">
            <div className="flex gap-3">
              <DateTile day={today.weekdayShort} date={today.day} month={today.monthShort} />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[14px] font-semibold text-[#172033]">Walk-a-thon</p>
                  <span className="rounded-full bg-[#EAF2EC] px-2.5 py-1 text-[10px] font-semibold text-[#2F6B48]">Booked ✓</span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <DemoChild name="Child A" />
                  <span className="text-[11px] font-medium text-[#4A5D4E]">View details ›</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-baseline justify-between">
            <h2 className="text-[17px] font-extrabold text-[#172033]">Action required</h2>
            <span className="text-[10px] text-[#596273]">1 item</span>
          </div>
          <div className="mt-2 rounded-2xl border border-[#E6E8EB] bg-white p-3.5 shadow-sm">
            <div className="flex gap-3">
              <DateTile day={action.weekdayShort} date={action.day} month={action.monthShort} />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[14px] font-semibold leading-tight text-[#172033]">Parent Breakfast Series</p>
                  <span className="rounded-full bg-[#FDEDE6] px-2.5 py-1 text-[9px] font-semibold text-[#C65334]">Action required</span>
                </div>
                <div className="mt-2"><DemoChild name="Child B" tone="blue" /></div>
                <p className="mt-2 text-[11px] font-semibold text-[#D95332]">Due {action.weekdayShort} {action.day} {action.monthShort} {action.year}</p>
                <div className="mt-3 flex items-center justify-between gap-2 text-[10px] text-[#667085]">
                  <span>Later · Not for us · View details ›</span>
                  <span className="rounded-full bg-[#173A67] px-3 py-1.5 font-semibold text-white">RSVP</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-baseline justify-between">
            <h2 className="text-[17px] font-extrabold text-[#172033]">Coming up</h2>
            <span className="text-[10px] text-[#596273]">2 items</span>
          </div>
          <div className="mt-2 rounded-2xl border border-[#E6E8EB] bg-white p-3.5 shadow-sm">
            <div className="flex gap-3">
              <DateTile day={upcoming.weekdayShort} date={upcoming.day} month={upcoming.monthShort} />
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-[#172033]">Year 3 &amp; 4 Camp</p>
                <div className="mt-2"><DemoChild name="Child A" tone="blue" /></div>
                <p className="mt-2 text-[10px] leading-relaxed text-[#667085]">Coming up — starts {upcoming.weekdayLong} {upcoming.day} {upcoming.monthLong} {upcoming.year}</p>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-[10px] font-medium text-[#4A5D4E]">View details</span>
                  <span className="rounded-full border border-[#CAD5CF] px-3 py-1.5 text-[10px] font-semibold text-[#2F5D3B]">＋ Add to calendar</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export function LandingShell() {
  return (
    <div data-testid="auth-shell" className="min-h-screen bg-[#FAFAF8] px-5 py-7 sm:px-8 sm:py-10 lg:px-12">
      <div data-testid="landing-mode-landing" className="mx-auto max-w-7xl">
        <main className="grid items-start gap-12">
          <section data-testid="landing-value-panel" className="max-w-2xl">
            <BrandHeader large />

            <h1
              data-testid="landing-heading"
              className="mt-8 text-[42px] font-extrabold tracking-[-0.035em] leading-[1.04] text-[#172033] sm:text-[54px] lg:text-[62px]"
            >
              Never miss a school event, activity or <span className="text-[#E36B4C]">deadline.</span>
            </h1>

            <p data-testid="landing-explanation" className="mt-6 max-w-xl text-[17px] leading-relaxed text-[#3E4A5B] sm:text-[19px]">
              FrontRow keeps track of the dates, bookings, forms and changes buried in school emails — so you know what needs doing and what&apos;s coming up.
            </p>

            <section data-testid="landing-benefits" className="mt-8 grid gap-6 sm:grid-cols-3">
              <Benefit icon={ClipboardCheck} title="Know what needs action" tone="coral">
                Bookings, forms, RSVPs and deadlines.
              </Benefit>
              <Benefit icon={CalendarCheck2} title="Know what&apos;s coming up" tone="blue">
                Events and activities, all in one place.
              </Benefit>
              <Benefit icon={BellRing} title="Know when something changes" tone="green">
                Times, venues and important updates.
              </Benefit>
            </section>

            <div data-testid="landing-cta-block" className="mt-9 max-w-lg">
              <Link
                to="/sign-up"
                data-testid="landing-cta-signup"
                className="block w-full rounded-2xl bg-[#082F5B] px-6 py-4 text-center text-[18px] font-semibold text-white shadow-sm transition-colors hover:bg-[#062649]"
              >
                Sign up
              </Link>
              <p className="mt-4 text-center text-[14px] text-[#5C625E]">
                Already have an account?{" "}
                <Link
                  to="/sign-in"
                  data-testid="landing-secondary-signin"
                  className="font-semibold text-[#173A67] underline underline-offset-2"
                >
                  Sign in
                </Link>
              </p>
            </div>

            <div className="mt-8 flex max-w-lg items-start gap-3 rounded-2xl bg-white/60 p-4 text-[#596273]">
              <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#5E6D7B]" strokeWidth={1.8} />
              <p className="text-[13px] leading-relaxed">
                <strong className="text-[#172033]">Cancel anytime.</strong><br />
                Choose Individual or Family when you join. Cancel anytime.
              </p>
            </div>
          </section>

          <section aria-label="FrontRow dashboard preview">
            <ProductPreview />
          </section>
        </main>

        <LegalFooter />
      </div>
    </div>
  );
}

export function AuthShellLayout() {
  const loc = useLocation();
  const mode = loc.pathname.startsWith("/sign-in") ? "sign-in" : "sign-up";
  return (
    <div data-testid="auth-shell" className="min-h-screen bg-[#FAFAF8] px-5 sm:px-8 py-8 sm:py-12">
      <div
        data-testid={`landing-mode-${mode}`}
        className={`${mode === "sign-up" ? "max-w-2xl" : "max-w-md"} mx-auto w-full`}
      >
        <BrandHeader />
        <h1
          data-testid="auth-page-heading"
          className="mt-6 text-[22px] sm:text-[24px] font-extrabold tracking-tight leading-tight text-[#172033]"
        >
          {mode === "sign-in" ? "Sign in" : "Create your FrontRow account"}
        </h1>
        <p data-testid="auth-page-subheading" className="mt-2 text-[13px] leading-relaxed text-[#5C625E]">
          {mode === "sign-in"
            ? "Welcome back. Sign in to open your dashboard."
            : "One quick step to unlock your dashboard."}
        </p>
        <div
          data-testid={`auth-clerk-container-${mode}`}
          className="mt-6 w-full flex justify-center"
        >
          <Outlet />
        </div>
        {mode === "sign-in" && (
          <p className="mt-6 text-center text-[13px] text-[#5C625E]">
            New to FrontRow?{" "}
            <Link to="/sign-up" data-testid="auth-secondary-signup" className="font-semibold text-[#4A5D4E] underline">
              Sign up
            </Link>
          </p>
        )}
        <LegalFooter />
      </div>
    </div>
  );
}

const CLERK_APPEARANCE = {
  elements: {
    rootBox: "w-full flex justify-center",
    card: "bg-white border border-[#E6E8EB] rounded-3xl shadow-sm px-5 sm:px-6 py-6 w-full max-w-[400px] mx-auto",
    headerTitle: "text-[16px] font-bold text-[#172033]",
    headerSubtitle: "text-[13px] text-[#5C625E]",
    formButtonPrimary:
      "bg-[#172033] hover:bg-[#0F2340] text-white rounded-full h-11 text-[14px] font-semibold w-full",
    socialButtonsBlockButton:
      "border border-[#E6E8EB] rounded-full h-11 text-[13px] font-semibold text-[#172033] hover:bg-[#F5F5F2]",
    footerAction: "text-[12px]",
  },
  variables: { colorPrimary: "#172033" },
};

const clerkErrorMessage = (error, fallback) => {
  const first = error?.errors?.[0];
  return first?.longMessage || first?.message || fallback;
};

export function SignInInner() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const [step, setStep] = useState("email");
  const [emailAddress, setEmailAddress] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const prepareInFlightRef = useRef(false);
  const verifyInFlightRef = useRef(false);

  const handlePrepare = async (event) => {
    event.preventDefault();
    if (!isLoaded || !signIn || prepareInFlightRef.current) return;
    prepareInFlightRef.current = true;
    setBusy(true);
    setError("");
    try {
      const attempt = await signIn.create({ identifier: emailAddress.trim() });
      const emailFactor = (attempt.supportedFirstFactors || []).find(
        (factor) => factor.strategy === "email_code"
      );
      if (!emailFactor?.emailAddressId) {
        setError("Email verification is not available for this account.");
        return;
      }
      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: emailFactor.emailAddressId,
      });
      setStep("verify");
    } catch (err) {
      setError(clerkErrorMessage(err, "We couldn't sign you in. Please check your email and try again."));
    } finally {
      prepareInFlightRef.current = false;
      setBusy(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!isLoaded || !signIn || !setActive || verifyInFlightRef.current) return;
    verifyInFlightRef.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "email_code",
        code: code.trim(),
      });
      if (result.status !== "complete" || !result.createdSessionId) {
        setError("That code could not be verified. Please check it and try again.");
        return;
      }
      await setActive({ session: result.createdSessionId });
    } catch (err) {
      setError(clerkErrorMessage(err, "That code could not be verified. Please check it and try again."));
    } finally {
      verifyInFlightRef.current = false;
      setBusy(false);
    }
  };

  if (!isLoaded) {
    return (
      <div data-testid="signin-inner" className="w-full">
        <div className="mx-auto w-full max-w-[400px] rounded-3xl border border-[#E6E8EB] bg-white px-6 py-8 text-center text-[13px] text-[#5C625E] shadow-sm">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="signin-inner" className="w-full">
      <div className="mx-auto w-full max-w-[400px] rounded-3xl border border-[#E6E8EB] bg-white px-6 py-7 shadow-sm">
        {step === "email" ? (
          <form onSubmit={handlePrepare} data-testid="custom-signin-email">
            <h2 className="text-center text-[18px] font-bold text-[#172033]">Welcome back</h2>
            <p className="mt-2 text-center text-[13px] text-[#5C625E]">Enter your email and we'll send one sign-in code.</p>
            <label className="mt-6 block text-[14px] font-semibold text-[#172033]" htmlFor="signin-email">Email address</label>
            <input
              id="signin-email"
              type="email"
              autoComplete="email"
              required
              value={emailAddress}
              onChange={(event) => setEmailAddress(event.target.value)}
              className="mt-2 h-12 w-full rounded-xl border border-[#D8DDE3] px-3.5 text-[15px] text-[#172033] outline-none focus:border-[#8294A6]"
            />
            {error && <p role="alert" className="mt-4 text-[13px] text-[#B42318]">{error}</p>}
            <button type="submit" disabled={busy} className="mt-6 h-12 w-full rounded-full bg-[#172033] text-[15px] font-semibold text-white disabled:opacity-60">
              {busy ? "Sending code…" : "Continue"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} data-testid="custom-signin-verify">
            <h2 className="text-center text-[18px] font-bold text-[#172033]">Check your email</h2>
            <p className="mt-2 text-center text-[13px] leading-relaxed text-[#5C625E]">
              Enter the verification code sent to <strong className="font-semibold text-[#172033]">{emailAddress}</strong>.
            </p>
            <p className="mt-2 text-center text-[13px] leading-relaxed text-[#5C625E]">
              Can’t find your code? Check your junk or spam folder.
            </p>
            <label className="mt-6 block text-[14px] font-semibold text-[#172033]" htmlFor="signin-code">Verification code</label>
            <input
              id="signin-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
              className="mt-2 h-12 w-full rounded-xl border border-[#D8DDE3] px-3.5 text-center text-[18px] tracking-[0.28em] text-[#172033] outline-none focus:border-[#8294A6]"
            />
            {error && <p role="alert" className="mt-4 text-[13px] text-[#B42318]">{error}</p>}
            <button type="submit" disabled={busy} className="mt-6 h-12 w-full rounded-full bg-[#172033] text-[15px] font-semibold text-white disabled:opacity-60">
              {busy ? "Verifying…" : "Continue"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function SignUpInner() {
  const { isLoaded, signUp, setActive } = useSignUp();
  const [step, setStep] = useState("details");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const createInFlightRef = useRef(false);
  const verifyInFlightRef = useRef(false);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!isLoaded || !signUp || createInFlightRef.current) return;

    createInFlightRef.current = true;
    setBusy(true);
    setError("");

    try {
      await signUp.create({
        emailAddress: emailAddress.trim(),
        password,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      });

      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });

      setStep("verify");
    } catch (err) {
      setError(clerkErrorMessage(err, "We couldn't create the account. Please check your details and try again."));
    } finally {
      createInFlightRef.current = false;
      setBusy(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!isLoaded || !signUp || !setActive || verifyInFlightRef.current) return;

    verifyInFlightRef.current = true;
    setBusy(true);
    setError("");

    try {
      const result = await signUp.attemptEmailAddressVerification({ code: code.trim() });
      if (result.status !== "complete" || !result.createdSessionId) {
        setError("That code could not be verified. Please check it and try again.");
        return;
      }
      await setActive({ session: result.createdSessionId });
    } catch (err) {
      setError(clerkErrorMessage(err, "That code could not be verified. Please check it and try again."));
    } finally {
      verifyInFlightRef.current = false;
      setBusy(false);
    }
  };

  if (!isLoaded) {
    return (
      <div data-testid="signup-inner" className="w-full">
        <div className="mx-auto w-full max-w-[620px] rounded-3xl border border-[#E6E8EB] bg-white px-6 py-8 text-center text-[13px] text-[#5C625E] shadow-sm sm:px-10">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="signup-inner" className="w-full">
      <div className="mx-auto w-full max-w-[620px] overflow-hidden rounded-[28px] border border-[#DDE2E7] bg-white shadow-[0_20px_50px_rgba(23,32,51,0.12)]">
        <div className="px-6 py-8 sm:px-10 sm:py-9">
          {step === "details" ? (
            <form onSubmit={handleCreate} data-testid="custom-signup-details">
              <div className="text-center">
                <h2 className="text-[22px] font-bold text-[#172033]">Create your account</h2>
                <p className="mt-2 text-[15px] text-[#5C625E]">Welcome! Please fill in the details to get started.</p>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-[14px] font-semibold text-[#172033]" htmlFor="signup-first-name">First name</label>
                    <span className="text-[12px] text-[#7A7D7B]">Optional</span>
                  </div>
                  <input
                    id="signup-first-name"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    className="mt-2 h-12 w-full rounded-xl border border-[#D8DDE3] px-3.5 text-[15px] text-[#172033] outline-none focus:border-[#8294A6]"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-[14px] font-semibold text-[#172033]" htmlFor="signup-last-name">Last name</label>
                    <span className="text-[12px] text-[#7A7D7B]">Optional</span>
                  </div>
                  <input
                    id="signup-last-name"
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    className="mt-2 h-12 w-full rounded-xl border border-[#D8DDE3] px-3.5 text-[15px] text-[#172033] outline-none focus:border-[#8294A6]"
                  />
                </div>
              </div>

              <label className="mt-6 block text-[14px] font-semibold text-[#172033]" htmlFor="signup-email">Email address</label>
              <input
                id="signup-email"
                type="email"
                autoComplete="email"
                required
                value={emailAddress}
                onChange={(event) => setEmailAddress(event.target.value)}
                className="mt-2 h-12 w-full rounded-xl border border-[#D8DDE3] px-3.5 text-[15px] text-[#172033] outline-none focus:border-[#8294A6]"
              />

              <label className="mt-6 block text-[14px] font-semibold text-[#172033]" htmlFor="signup-password">Password</label>
              <div className="relative mt-2">
                <input
                  id="signup-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your password"
                  className="h-12 w-full rounded-xl border border-[#D8DDE3] px-3.5 pr-12 text-[15px] text-[#172033] outline-none placeholder:text-[#8A8F98] focus:border-[#8294A6]"
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-[#8A8F98] hover:text-[#596273]"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {error && <p role="alert" className="mt-4 text-[13px] text-[#B42318]">{error}</p>}

              <button
                type="submit"
                disabled={busy}
                className="mt-7 h-12 w-full rounded-full bg-[#172033] text-[15px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? "Creating account…" : "Continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify} data-testid="custom-signup-verify">
              <div className="text-center">
                <h2 className="text-[22px] font-bold text-[#172033]">Verify your email</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-[#5C625E]">
                  Enter the verification code sent to <strong className="font-semibold text-[#172033]">{emailAddress}</strong>.
                </p>
                <p className="mt-2 text-[15px] leading-relaxed text-[#5C625E]">
                  Can’t find your code? Check your junk or spam folder.
                </p>
              </div>

              <label className="mt-8 block text-[14px] font-semibold text-[#172033]" htmlFor="signup-code">Verification code</label>
              <input
                id="signup-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className="mt-2 h-12 w-full rounded-xl border border-[#D8DDE3] px-3.5 text-center text-[18px] tracking-[0.28em] text-[#172033] outline-none focus:border-[#8294A6]"
              />

              {error && <p role="alert" className="mt-4 text-[13px] text-[#B42318]">{error}</p>}

              <button
                type="submit"
                disabled={busy}
                className="mt-7 h-12 w-full rounded-full bg-[#172033] text-[15px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? "Verifying…" : "Continue"}
              </button>
            </form>
          )}
        </div>

        <div className="border-t border-[#E6E8EB] bg-[#FBFBFA] px-6 py-5 text-center text-[14px] text-[#5C625E]">
          Already have an account?{" "}
          <Link to="/sign-in" data-testid="auth-secondary-signin" className="font-semibold text-[#172033] underline underline-offset-2">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
